/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useSetRecoilState } from "recoil";
import { updateCampaign } from "../../../api/Campaign";
import {
  ActiveCampaignAtom,
  FilteredCampaignsAtom,
  TempCampaignAtom,
} from "../../../atoms/CampaignAtom";
import { UserAtom } from "../../../atoms/UserAtom";
import { ICampaignOptions, IFixedDateStrategy } from "../../../generated/Api";
import { SpaceBetween } from "../../../styles/general";
import { Campaign } from "../../../types/CampaignTypes";
import { IOption } from "../../../types/CommonTypes";
import StyledAutocompleteSelect from "../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../StyledButton";
import Switch from "../../Switch";
import { useModal } from "../Modal";

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: 1px solid #eceeee;
  box-sizing: inherit;
  margin: 20px -22px 0px;
  width: 600px;
  overflow-x: hidden;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: calc(100% - 94px);
    margin: 0;

    border-top: none;
  }
`;

const Container = styled.div`
  width: 100%;
  height: calc(100% - 67px);

  padding: 32px;

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: scroll;
  overflow-x: hidden;

  .line {
    width: 100%;
    height: 1px;

    background-color: #eceeef;

    margin: 40px 0;
  }

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: calc(100% - 94px);
    min-height: 100%;
    margin: 0;
  }
`;

const FormWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  grid-gap: 8px;

  &.mt-24 {
    margin-top: 24px;
  }

  &.radio {
    grid-gap: 16px;

    .text {
      font-size: 16px;
      font-weight: 400;
      color: #333333;
    }
  }

  &.switch {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  &.option {
    margin-top: 12px;
  }

  .label {
    font-size: 16px;
    font-weight: 600;
    color: #333333;
  }

  input {
    width: 100%;
  }
`;

const Buttons = styled.div`
  width: 100%;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  grid-gap: 16px;

  @media screen and (max-width: 1024px) {
    border-top: 1px solid #cccccc;

    padding: 25px 25px 0;

    position: absolute;
    bottom: 25px;
    left: 0;
    right: 0;

    justify-content: flex-end;
  }
`;

interface CampaignSettingsProps {
  campaignOptions: ICampaignOptions;
  campaignId: string;
}

export default function CampaignSettings({
  campaignOptions,
  campaignId,
}: CampaignSettingsProps): JSX.Element {
  const modal = useModal();

  const [user, setUser] = useRecoilState(UserAtom);

  const initialSelectedFooter = user.emailOptions.footers.find(
    (f) => f.id === campaignOptions.defaultFooterId
  );
  const initialSelectedAlias = user.emailOptions.aliases.find(
    (a) => a.id === campaignOptions.defaultAliasId
  );

  const [selectedFooter, setSelectedFooter] = useState<IOption>({
    value: campaignOptions.defaultFooterId,
    name: initialSelectedFooter.name,
  });
  const [selectedAlias, setSelectedAlias] = useState<IOption>({
    value: campaignOptions.defaultAliasId,
    name: `${initialSelectedAlias.fromName} <${initialSelectedAlias.address}>`,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [fixedDateStrategy, setFixedDateStrategy] =
    useState<IFixedDateStrategy>(campaignOptions.fixedDateStrategy);
  const [preventReentering, setPreventReentering] = useState(
    campaignOptions.preventReentering
  );
  const [overrideFooters, setOverrideFooters] = useState(
    campaignOptions.overrideFooters
  );

  const [tempCampaign, setTempCampaign] = useRecoilState<Campaign | null>(
    TempCampaignAtom
  );
  const [activeCampaign, setActiveCampaign] = useRecoilState<Campaign | null>(
    ActiveCampaignAtom
  );
  const setFilteredCampaigns = useSetRecoilState<Campaign[] | null>(
    FilteredCampaignsAtom
  );

  const footerOptions = useMemo(
    () =>
      user.emailOptions.footers.map((f) => ({
        value: f.id as string,
        name: f.name as string,
      })),
    [user.emailOptions.footers]
  );

  const aliasOptions = useMemo(
    () =>
      user.emailOptions.aliases.map((a) => ({
        value: a.id as string,
        name: `${a.fromName} <${a.address}>`,
      })),
    [user.emailOptions.aliases]
  );

  async function onClickSave() {
    try {
      setIsLoading(true);
      const newCampaignOptions = {
        defaultAliasId: selectedAlias.value,
        defaultFooterId: selectedFooter.value,
        fixedDateStrategy,
        fixed: false,
        preventReentering,
        overrideFooters,
      };
      await updateCampaign(campaignId, {
        campaignOptions: newCampaignOptions,
      });
      if (tempCampaign) {
        setTempCampaign({
          ...tempCampaign,
          campaignOptions: newCampaignOptions,
        });
      }
      if (activeCampaign) {
        setActiveCampaign({
          ...activeCampaign,
          campaignOptions: newCampaignOptions,
        });
      }
      setFilteredCampaigns((filteredCampaigns) =>
        filteredCampaigns.map((c) =>
          c._id === campaignId
            ? { ...c, campaignOptions: newCampaignOptions }
            : c
        )
      );
      toast("Campaign options updated", { type: "success" });
      modal.popModal();
    } catch (error) {
      toast("Something wrong has occurred", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  function onClickCancel() {
    modal.popModal();
  }

  return (
    <ModalBody>
      <Container>
        <FormWrapper>
          <div className="label">Default Footer</div>
          <StyledAutocompleteSelect
            options={footerOptions}
            selectedOption={selectedFooter}
            onOptionSelect={(option) => {
              if (option.value) setSelectedFooter(option);
            }}
            placeholder={"Select Footer"}
          />
        </FormWrapper>
        <FormWrapper className="switch option">
          <div className="label">Override other footers</div>

          <Switch
            toggled={overrideFooters}
            whenToggled={setOverrideFooters}
            texts={["Off", "On"]}
          />
        </FormWrapper>

        <FormWrapper className="mt-24">
          <div className="label">Default Send-From Email Address</div>
          <StyledAutocompleteSelect
            options={aliasOptions}
            selectedOption={selectedAlias}
            onOptionSelect={(option) => {
              if (option.value) setSelectedAlias(option);
            }}
            placeholder={"Default Send-From Email Address"}
          />
        </FormWrapper>

        {/* <div className="line" /> */}

        {/* <FormWrapper className="radio">
          <div className="label">Fixed Dates</div>
          <div className="text">If the date has already passed…</div>
          @ts-ignore
          <RadioGroup onChange={setFixedDateStrategy} value={fixedDateStrategy}>
            <VStack alignItems="flex-start">
              <Radio
                value={IFixedDateStrategy.Jump}
                size="md"
                name={IFixedDateStrategy.Jump}
                colorScheme="orange"
              >
                Skip over steps and go to the next
              </Radio>
              <Radio
                value={IFixedDateStrategy.Block}
                size="md"
                name={IFixedDateStrategy.Block}
                colorScheme="orange"
              >
                Stop at the delay step
              </Radio>
            </VStack>
          </RadioGroup>
        </FormWrapper> */}

        <div className="line" />

        <FormWrapper className="switch">
          <div className="label">Prevent from re-entering</div>

          <Switch
            toggled={preventReentering}
            whenToggled={setPreventReentering}
            texts={["Off", "On"]}
          />
        </FormWrapper>
      </Container>

      <SpaceBetween
        css={css`
          border-top: 1px solid #eceeee;
          padding: 24px 20px 0px;

          @media screen and (max-width: 1024px) {
            border-top: none;
            padding: 0;
          }
        `}
      >
        <div />

        <Buttons>
          <StyledButton
            inverted
            onClick={onClickCancel}
            style={{ width: "92px" }}
          >
            Cancel
          </StyledButton>

          <StyledButton
            isLoading={isLoading}
            style={{ width: "92px" }}
            onClick={onClickSave}
          >
            Save
          </StyledButton>
        </Buttons>
      </SpaceBetween>
    </ModalBody>
  );
}
